import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Store as StoreIcon, Loader2, Upload } from "lucide-react";
import { api, errMsg, PLATFORMS } from "@/lib/api";
import { PageHeader, Panel, PlatformBadge, AuthImage } from "@/components/Shared";
import { Field, Select, inputCls, areaCls, btnPrimary, btnGhost } from "@/components/Form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", platform: "99food", policies: "", products: [], photo_ids: [] };
const EMPTY_P = { name: "", description: "", combo: "", weight: "", price: "" };

function StoreForm({ initial, onSaved }) {
  const [s, setS] = useState(() => ({ ...initial, products: initial.products.map((p) => ({ ...p, _k: crypto.randomUUID() })) }));
  const [busy, setBusy] = useState(false);
  const setP = (i, k, v) => setS({ ...s, products: s.products.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const upload = async (files) => {
    const ids = [];
    for (const file of Array.from(files)) {
      const fd = new FormData(); fd.append("file", file); fd.append("evidence_type", "foto_oficial");
      try { const { data } = await api.post("/files", fd); ids.push(data.file_id); } catch (e) { toast.error(errMsg(e)); }
    }
    setS((x) => ({ ...x, photo_ids: [...x.photo_ids, ...ids] }));
  };
  const save = async () => {
    if (!s.name.trim()) return toast.error("Informe o nome da loja");
    setBusy(true);
    try {
      s.store_id ? await api.put(`/stores/${s.store_id}`, s) : await api.post("/stores", s);
      toast.success("Loja salva"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nome da loja"><input data-testid="store-name-input" className={inputCls} value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} /></Field>
        <Field label="Plataforma"><Select testid="store-platform-select" options={PLATFORMS} value={s.platform} onChange={(e) => setS({ ...s, platform: e.target.value })} /></Field>
      </div>
      <Field label="Políticas e procedimentos internos" hint="Ex.: todos os pedidos saem lacrados e fotografados antes da coleta."><textarea data-testid="store-policies-input" rows={3} className={areaCls} value={s.policies} onChange={(e) => setS({ ...s, policies: e.target.value })} /></Field>
      <div>
        <div className="mb-2 flex items-center justify-between"><p className="text-sm font-bold text-slate-700">Cardápio / produtos ({s.products.length})</p>
          <button type="button" data-testid="add-product-btn" onClick={() => setS({ ...s, products: [...s.products, { ...EMPTY_P, _k: crypto.randomUUID() }] })} className={btnGhost}><Plus className="h-4 w-4" /> Produto</button></div>
        <div className="space-y-3">{s.products.map((p, i) => (
          <div key={p._k} className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-2" data-testid={`product-row-${i}`}>
            <input data-testid={`product-name-${i}`} placeholder="Nome do produto" className={inputCls} value={p.name} onChange={(e) => setP(i, "name", e.target.value)} />
            <input data-testid={`product-weight-${i}`} placeholder="Peso/quantidade anunciados" className={inputCls} value={p.weight} onChange={(e) => setP(i, "weight", e.target.value)} />
            <input placeholder="Descrição do anúncio" className={`${inputCls} sm:col-span-2`} value={p.description} onChange={(e) => setP(i, "description", e.target.value)} data-testid={`product-desc-${i}`} />
            <input placeholder="Composição do combo" className={inputCls} value={p.combo} onChange={(e) => setP(i, "combo", e.target.value)} data-testid={`product-combo-${i}`} />
            <div className="flex gap-2"><input placeholder="Preço" className={inputCls} value={p.price} onChange={(e) => setP(i, "price", e.target.value)} data-testid={`product-price-${i}`} />
              <button type="button" onClick={() => setS({ ...s, products: s.products.filter((_, j) => j !== i) })} className="px-2 text-slate-400 hover:text-red-600" data-testid={`product-remove-${i}`}><Trash2 className="h-4 w-4" /></button></div>
          </div>))}</div>
      </div>
      <div>
        <p className="mb-2 text-sm font-bold text-slate-700">Fotos oficiais</p>
        <div className="flex flex-wrap gap-2">
          {s.photo_ids.map((id) => <AuthImage key={id} fileId={id} className="h-16 w-16 rounded-md object-cover" />)}
          <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-300 text-slate-400 hover:border-amber-500"><Upload className="h-5 w-5" />
            <input data-testid="store-photo-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => upload(e.target.files)} /></label>
        </div>
      </div>
      <button data-testid="store-save-btn" onClick={save} disabled={busy} className={`${btnPrimary} w-full`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar loja</button>
    </div>
  );
}

export default function Stores() {
  const [stores, setStores] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/stores").then((r) => setStores(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Excluir esta loja?")) return; await api.delete(`/stores/${id}`); load(); };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Cadastro" title="Lojas e cardápio" desc="Produtos, pesos e combos cadastrados ajudam a comparar anúncio e reclamação.">
        <button data-testid="new-store-btn" onClick={() => setEditing({ ...EMPTY })} className="inline-flex h-12 items-center gap-2 rounded-lg bg-amber-500 px-5 font-bold text-white hover:bg-amber-600"><Plus className="h-5 w-5" /> Nova loja</button>
      </PageHeader>
      {stores === null ? <div className="h-40 animate-pulse rounded-xl bg-slate-200" /> : stores.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="stores-empty">Nenhuma loja cadastrada ainda.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="stores-list">{stores.map((s) => (
          <Panel key={s.store_id} testid={`store-card-${s.store_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-3"><div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-900"><StoreIcon className="h-6 w-6 text-amber-400" /></div>
                <div><p className="font-display text-lg font-bold text-slate-900">{s.name}</p><PlatformBadge platform={s.platform} /></div></div>
              <div className="flex gap-1">
                <button data-testid={`edit-store-${s.store_id}`} onClick={() => setEditing(s)} className="p-2 text-slate-500 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-store-${s.store_id}`} onClick={() => del(s.store_id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-500">{s.products.length} produto(s) · {s.photo_ids.length} foto(s)</p>
            {s.policies && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{s.policies}</p>}
          </Panel>))}</div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing?.store_id ? "Editar loja" : "Nova loja"}</DialogTitle></DialogHeader>
          {editing && <StoreForm initial={editing} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
